/**
 * Alert Store — Zustand store for unified alert system
 * TASK-381: Manages alert state, counts, and live updates
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { DashboardAlert, AlertCounts } from '../types/dashboard';
import { alertService, type AlertListParams } from '../services/alertService';

/**
 * ONE page size for the alert list, product-wide (deep review #5).
 *
 * TASK-653 makes every surface show the same primary number — unresolved alert GROUPS — and they
 * all derive it from this one `alerts` array. So whoever fetched last decides the number. The
 * bell and the Alerts page asked for 200; `ChangeRadar` and the `radar_updated` handler took the
 * API's 50 default, and on an account with 523 alerts that is 10 groups instead of 11. The number
 * changed on navigation, and (since the `radar_updated` refetch) on every radar tick. Anything
 * that fills this store must use this constant — it is also the API's `le=200` ceiling.
 */
export const ALERT_PAGE_SIZE = 200;

interface AlertStoreState {
  // State
  alerts: DashboardAlert[];
  counts: AlertCounts;
  total: number;
  loading: boolean;
  error: string | null;

  // Actions
  fetchAlerts: (params?: AlertListParams) => Promise<void>;
  fetchCounts: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  snooze: (id: string, until: string) => Promise<void>;
  /** TASK-732: policy-valid acceptance. THROWS on refusal so the caller can show why. */
  resolve: (id: string, rationale?: string) => Promise<void>;
  addAlert: (alert: DashboardAlert) => void;
  updateAlertStatus: (alertId: string, status: string) => void;
  reset: () => void;
}

const initialCounts: AlertCounts = {
  critical: 0,
  high: 0,
  medium: 0,
  info: 0,
  total: 0,
  unread: 0,
};

const initialState = {
  alerts: [] as DashboardAlert[],
  counts: initialCounts,
  total: 0,
  loading: false,
  error: null as string | null,
};

export const useAlertStore = create<AlertStoreState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchAlerts: async (params) => {
        set({ loading: true, error: null });
        try {
          // TASK-801: `unresolved` is defaulted HERE, not at the five call sites. One store
          // backs the bell, the Alerts page, the Radar rail, the meetings widget and the WS
          // refresh; if some callers asked for the unresolved population and others did not,
          // the store's contents would depend on whichever fetched last. A caller that
          // genuinely wants history can still pass `unresolved: false`.
          const data = await alertService.list({ unresolved: true, ...params });
          set({
            alerts: data.alerts,
            total: data.total,
            loading: false,
          });
        } catch (e: any) {
          const status = e?.response?.status;
          // 503/502/504 = service not running yet — degrade silently
          if (status === 503 || status === 502 || status === 504) {
            set({ loading: false });
            return;
          }
          set({ error: e.message || 'Failed to fetch alerts', loading: false });
        }
      },

      fetchCounts: async () => {
        try {
          const counts = await alertService.getCounts();
          set({ counts });
        } catch {
          // Counts failure is non-critical — silent degradation
        }
      },

      markAsRead: async (id) => {
        try {
          await alertService.markAsRead(id);
          set((state) => ({
            alerts: state.alerts.map((a) =>
              a.id === id ? { ...a, status: 'read' as const, isRead: true } : a
            ),
            counts: {
              ...state.counts,
              unread: Math.max(0, state.counts.unread - 1),
            },
          }));
        } catch (e: any) {
          console.warn('Failed to mark alert as read:', e);
        }
      },

      dismiss: async (id) => {
        try {
          await alertService.dismiss(id);
          set((state) => {
            const alert = state.alerts.find((a) => a.id === id);
            const level = alert?.level;
            return {
              alerts: state.alerts.map((a) =>
                a.id === id ? { ...a, status: 'dismissed' as const, isDismissed: true } : a
              ),
              counts: {
                ...state.counts,
                unread: Math.max(0, state.counts.unread - 1),
                total: Math.max(0, state.counts.total - 1),
                ...(level ? { [level]: Math.max(0, (state.counts[level] || 0) - 1) } : {}),
              },
            };
          });
        } catch (e: any) {
          console.warn('Failed to dismiss alert:', e);
        }
      },

      snooze: async (id, until) => {
        try {
          await alertService.snooze(id, until);
          set((state) => ({
            alerts: state.alerts.map((a) =>
              a.id === id ? { ...a, status: 'snoozed' as const, snoozedUntil: until } : a
            ),
            counts: {
              ...state.counts,
              unread: Math.max(0, state.counts.unread - 1),
            },
          }));
        } catch (e: any) {
          console.warn('Failed to snooze alert:', e);
        }
      },

      // TASK-732: resolve is a policy-enforced acceptance. Unlike dismiss/snooze it
      // RETHROWS — the caller collected a rationale and must be told when the server
      // refuses it (wrong role, missing rationale), not left believing it worked.
      resolve: async (id, rationale) => {
        await alertService.resolve(id, rationale);
        set((state) => {
          const alert = state.alerts.find((a) => a.id === id);
          const level = alert?.level;
          return {
            alerts: state.alerts.map((a) =>
              a.id === id ? { ...a, status: 'resolved' as const } : a
            ),
            counts: {
              ...state.counts,
              unread: Math.max(0, state.counts.unread - 1),
              total: Math.max(0, state.counts.total - 1),
              ...(level ? { [level]: Math.max(0, (state.counts[level] || 0) - 1) } : {}),
            },
          };
        });
      },

      // Called from WebSocket on new_alert (W6: dedup check)
      addAlert: (alert) => {
        set((state) => {
          if (state.alerts.some((a) => a.id === alert.id)) return state;
          return {
            alerts: [alert, ...state.alerts],
            counts: {
              ...state.counts,
              total: state.counts.total + 1,
              unread: state.counts.unread + 1,
              [alert.level]: (state.counts[alert.level] || 0) + 1,
            },
          };
        });
      },

      // Called from WebSocket on alert_updated (W7: adjust counts)
      updateAlertStatus: (alertId, status) => {
        set((state) => {
          const existing = state.alerts.find((a) => a.id === alertId);
          if (!existing) return state;

          const wasActive = existing.status === 'active' || (!existing.status && !existing.isRead && !existing.isDismissed);
          const isNowInactive = status === 'dismissed' || status === 'resolved' || status === 'snoozed';
          const isNowDismissedOrResolved = status === 'dismissed' || status === 'resolved';

          const countsDelta: Partial<typeof state.counts> = {};
          if (wasActive && status !== 'active') {
            countsDelta.unread = Math.max(0, state.counts.unread - 1);
          }
          if (isNowDismissedOrResolved) {
            countsDelta.total = Math.max(0, state.counts.total - 1);
            if (existing.level) {
              countsDelta[existing.level] = Math.max(0, (state.counts[existing.level] || 0) - 1);
            }
          }

          return {
            alerts: state.alerts.map((a) =>
              a.id === alertId
                ? {
                    ...a,
                    status: status as DashboardAlert['status'],
                    isRead: status === 'read' || isNowInactive,
                    isDismissed: status === 'dismissed',
                  }
                : a
            ),
            counts: { ...state.counts, ...countsDelta },
          };
        });
      },

      reset: () => set(initialState),
    }),
    { name: 'AlertStore' }
  )
);

// Shared helper (D2: single source of truth for active check)
export const isAlertActive = (alert: DashboardAlert): boolean => {
  if (alert.status) return alert.status === 'active';
  return !alert.isDismissed && !alert.isRead;
};

// Selector hooks for optimized re-renders
export const useAlertCounts = () => useAlertStore((s) => s.counts);
export const useAlertUnreadCount = () => useAlertStore((s) => s.counts.unread);
export const useAlerts = () => useAlertStore((s) => s.alerts);
export const useAlertLoading = () => useAlertStore((s) => s.loading);
